import { useState, useEffect } from 'react';
import { getSaisons, getStatsSaison, getMatch, getMatches, getStatsJoueur, getStatsGardien, getEquipePublique, urlLogo } from '../api';
import { themeCourant } from '../theme';

/** Couleurs du club pour les graphiques, mises à jour quand le thème change. */
function useCouleurs() {
  const [t, setT] = useState(themeCourant());
  useEffect(() => {
    const maj = e => setT(e.detail || themeCourant());
    window.addEventListener('theme-change', maj);
    return () => window.removeEventListener('theme-change', maj);
  }, []);
  return t;
}
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';

const pctColor  = v => v >= 60 ? '#22c55e' : v >= 40 ? '#eab308' : '#ef4444';
const gkColor   = v => v >= 35 ? '#22c55e' : v >= 28 ? '#eab308' : '#ef4444';
const rankColor = i => ['#f59e0b','#9ca3af','#b45309'][i] || '#8b949e';
const resLabel  = r => r === 'V' ? 'Victoire' : r === 'N' ? 'Nul' : 'Défaite';

export default function PublicStats() {
  const [tab, setTab]                 = useState('saison');
  const [saisons, setSaisons]         = useState([]);
  const [saison, setSaison]           = useState(null);
  const [data, setData]               = useState(null);
  const [matches, setMatches]         = useState([]);
  const [selMatch, setSelMatch]       = useState(null);
  const [matchDetail, setMatchDetail] = useState(null);
  const [selJoueur, setSelJoueur]     = useState(null);
  const [joueurEvol, setJoueurEvol]   = useState([]);
  const [loading, setLoading]         = useState(true);

  const [club, setClub] = useState({ club: '', equipe: '' });
  const couleurs = useCouleurs();
  useEffect(() => { getEquipePublique().then(e => e && setClub(e)).catch(() => {}); }, []);
  const [privee, setPrivee] = useState(false);
  const [erreur, setErreur] = useState(null);
  useEffect(() => { getSaisons().then(s => { setSaisons(s); setSaison(s[0] || null); if (!s.length) setLoading(false); })
                     .catch(e => { if (e.code === 'STATS_PRIVEES') setPrivee(true); else setErreur(e.message); setLoading(false); }); }, []);

  useEffect(() => {
    if (saison === null && saisons.length === 0) return;
    setLoading(true);
    Promise.all([getStatsSaison(saison), getMatches(saison)])
      .then(([stats, list]) => {
        setData(stats); setMatches(list); setPrivee(false);
        if (list.length) setSelMatch(list[0].id);
      })
      .catch(e => { if (e.code === 'STATS_PRIVEES') setPrivee(true); else setErreur(e.message); })
      .finally(() => setLoading(false));
  }, [saison]);

  useEffect(() => { if (selMatch) getMatch(selMatch).then(setMatchDetail); }, [selMatch]);
  useEffect(() => { if (selJoueur) getStatsJoueur(selJoueur, saison).then(setJoueurEvol); }, [selJoueur, saison]);

  if (loading) return <div style={{ padding:40, textAlign:'center', color:'#8b949e' }}>Chargement…</div>;
  if (privee) return (
    <div style={{ padding:'56px 24px', textAlign:'center', maxWidth:400, margin:'0 auto' }}>
      <div style={{ fontSize:36, marginBottom:12 }}>🔒</div>
      <h1 style={{ fontFamily:'Barlow Condensed', fontSize:22, fontWeight:800 }}>Statistiques réservées aux membres</h1>
      <p style={{ color:'var(--muted)', fontSize:14, lineHeight:1.7, marginTop:10 }}>
        {club.club || 'Ce club'} a choisi de ne pas rendre ses statistiques publiques. Connectez-vous pour les consulter.</p>
    </div>);
  if (!data) return (
    <div style={{ padding:'56px 24px', textAlign:'center', maxWidth:420, margin:'0 auto' }}>
      <div style={{ fontSize:34, marginBottom:12 }}>📊</div>
      <h1 style={{ fontFamily:'Barlow Condensed', fontSize:22, fontWeight:800 }}>
        {erreur ? 'Statistiques indisponibles' : 'Pas encore de statistiques'}</h1>
      <p style={{ color:'var(--muted)', fontSize:14, lineHeight:1.7, marginTop:10 }}>
        {erreur === 'Équipe à préciser'
          ? 'Aucune équipe sélectionnée. Choisissez-en une dans la barre du haut, ou créez un club depuis la console.'
          : erreur || 'Les statistiques apparaîtront après l\'import de la première feuille de match.'}</p>
    </div>);

  const { equipe, joueurs, gardiens, evolution, tirs } = data;

  return (
    <>
      <div className="page-header">
        <div className="eyebrow">2ème Division Territoriale +16M</div>
        <h1 style={{ display:'flex', alignItems:'center', gap:12 }}>
          {club.logo_url && <img src={urlLogo(club.logo_url)} alt="" style={{ height:44, width:44, objectFit:'contain', borderRadius:8 }} />}
          {(club.club || '').toUpperCase()}
        </h1>
        <div className="sub">Saison {saison}</div>
        <div className="pill-bar">
          {saisons.map(s => (
            <button key={s} className={`pill ${saison === s ? 'active' : ''}`} onClick={() => setSaison(s)}>{s}</button>
          ))}
        </div>
      </div>

      <div className="kpi-bar">
        <div className="kpi"><div className="val">{equipe.matchs_joues}</div><div className="lbl">Matchs</div></div>
        <div className="kpi win"><div className="val">{equipe.victoires}</div><div className="lbl">Victoires</div></div>
        <div className="kpi"><div className="val">{equipe.nuls}</div><div className="lbl">Nuls</div></div>
        <div className="kpi loss"><div className="val">{equipe.defaites}</div><div className="lbl">Défaites</div></div>
        <div className="kpi"><div className="val">{equipe.buts_marques}</div><div className="lbl">Buts +</div></div>
        <div className="kpi"><div className="val">{equipe.buts_encaisses}</div><div className="lbl">Buts -</div></div>
        <div className="kpi"><div className="val">{equipe.moy_buts_pour}</div><div className="lbl">Moy/match</div></div>
      </div>

      <div className="tabs">
        {[['saison','📊 Saison'],['match','⚽ Match'],['gardien','🧤 Gardien']].map(([k,l]) => (
          <button key={k} className={`tab-btn ${tab===k?'active':''}`} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>

      {tab === 'saison' && (
        <>
          <div className="section">
            <div className="section-title">Résultats</div>
            {matches.map(m => (
              <div className="card card-row" key={m.id}>
                <div>
                  <div className="card-meta">{m.date} · {m.journee} · {m.dom_ext}</div>
                  <div className="card-title">vs {m.adversaire}</div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <span className="card-score">{m.score_nous} <span style={{ color:'#8b949e', fontWeight:400 }}>-</span> {m.score_eux}</span>
                  <span className={`badge badge-${m.resultat}`}>{resLabel(m.resultat)}</span>
                </div>
              </div>
            ))}
          </div>

          {tirs && (tirs.attaque.tentatives > 0 || tirs.defense.tentatives > 0) && (
            <div className="section" style={{ paddingTop:4 }}>
              <div className="section-title">Bilan des tirs</div>
              <BilanTirs tirs={tirs} />
            </div>
          )}

          {evolution.length > 1 && (
            <div className="section" style={{ paddingTop:4 }}>
              <div className="section-title">Évolution des scores</div>
              <div className="card" style={{ padding:'14px 6px' }}>
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={evolution}>
                    <CartesianGrid stroke="#21262d" strokeDasharray="3 3" />
                    <XAxis dataKey="journee" tick={{ fill:'#8b949e', fontSize:11 }} />
                    <YAxis tick={{ fill:'#8b949e', fontSize:11 }} />
                    <Tooltip contentStyle={{ background:'#161b22', border:'1px solid #21262d', borderRadius:8, fontSize:12 }} />
                    <Line type="monotone" dataKey="score_nous" stroke={couleurs.accent} strokeWidth={2} dot={{ r:4 }} name="Nous" />
                    <Line type="monotone" dataKey="score_eux"  stroke={couleurs.chart2} strokeWidth={2} dot={{ r:4 }} name="Eux" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <div className="section" style={{ paddingTop:4 }}>
            <div className="section-title">Classement buteurs</div>
            <div className="card" style={{ padding:'6px 0' }}>
              <table className="stats-table">
                <thead><tr><th>#</th><th>Joueur</th><th>Buts</th><th>Tirs</th><th>Eff.</th></tr></thead>
                <tbody>
                  {joueurs.map((j, i) => (
                    <>
                      <tr key={j.nom} onClick={() => setSelJoueur(selJoueur === j.nom ? null : j.nom)}
                        style={{ cursor:'pointer', background: selJoueur===j.nom ? 'rgba(var(--accent-rgb),0.06)' : undefined }}>
                        <td style={{ color: rankColor(i), fontWeight:800 }}>{i+1}</td>
                        <td>
                          <div className="player-cell">
                            <span className="num-badge">{j.numero}</span>
                            <span className="player-name">{j.nom}</span>
                            {selJoueur === j.nom && <span style={{ fontSize:9, color:'var(--accent)' }}>▼</span>}
                          </div>
                        </td>
                        <td><span className="buts-val">{j.buts}</span></td>
                        <td>{j.tirs}</td>
                        <td>
                          <div className="pct-wrap">
                            <span className="pct-num" style={{ color:pctColor(j.pct_efficacite||0) }}>{j.pct_efficacite||0}%</span>
                            <div className="pct-bar"><div className="pct-fill" style={{ width:`${j.pct_efficacite||0}%`, background:pctColor(j.pct_efficacite||0) }} /></div>
                          </div>
                        </td>
                      </tr>
                      {selJoueur === j.nom && joueurEvol.length > 0 && (
                        <tr key={`${j.nom}-g`}>
                          <td colSpan={5} style={{ padding:'14px 8px', background:'rgba(var(--accent-rgb),0.03)' }}>
                            <div style={{ fontSize:11, color:'var(--accent)', fontWeight:700, marginBottom:8, letterSpacing:1, textTransform:'uppercase' }}>
                              Évolution — {j.nom}
                            </div>
                            <ResponsiveContainer width="100%" height={190}>
                              <LineChart data={joueurEvol.map(e => ({ match:e.journee||e.date, Buts:e.buts, Tirs:e.tirs, 'Eff.%':e.pct_efficacite||0 }))}>
                                <CartesianGrid stroke="#21262d" strokeDasharray="3 3" />
                                <XAxis dataKey="match" tick={{ fill:'#8b949e', fontSize:10 }} />
                                <YAxis tick={{ fill:'#8b949e', fontSize:10 }} />
                                <Tooltip contentStyle={{ background:'#161b22', border:'1px solid #21262d', borderRadius:8, fontSize:11 }} />
                                <Legend wrapperStyle={{ fontSize:11 }} />
                                <Line type="monotone" dataKey="Buts"  stroke={couleurs.accent} strokeWidth={2} dot={{ r:3 }} />
                                <Line type="monotone" dataKey="Tirs"  stroke="#3b82f6" strokeWidth={2} dot={{ r:3 }} />
                                <Line type="monotone" dataKey="Eff.%" stroke="#22c55e" strokeWidth={2} dot={{ r:3 }} strokeDasharray="4 2" />
                              </LineChart>
                            </ResponsiveContainer>
                            <div style={{ overflowX:'auto', marginTop:8 }}>
                              <table className="stats-table" style={{ fontSize:12 }}>
                                <thead><tr><th>Match</th><th>Adversaire</th><th>Buts</th><th>Tirs</th><th>Eff.</th></tr></thead>
                                <tbody>
                                  {joueurEvol.map((e,k) => (
                                    <tr key={k}>
                                      <td>{e.journee||e.date}</td>
                                      <td style={{ fontSize:11 }}>{e.adversaire}</td>
                                      <td style={{ fontWeight:800, color:'var(--accent)' }}>{e.buts}</td>
                                      <td>{e.tirs}</td>
                                      <td style={{ color:pctColor(e.pct_efficacite||0) }}>{e.pct_efficacite||0}%</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {tab === 'match' && (
        <div className="section">
          <div className="section-title">Sélectionner un match</div>
          <div className="pill-bar" style={{ marginTop:0, marginBottom:14 }}>
            {matches.map(m => (
              <button key={m.id} className={`pill ${selMatch===m.id?'active':''}`} onClick={() => setSelMatch(m.id)}>
                {m.journee||m.date} — {m.adversaire}
              </button>
            ))}
          </div>

          {matchDetail && (
            <>
              <div className="card" style={{ textAlign:'center', background:'linear-gradient(135deg,#161b22,#0d1117)', marginBottom:14 }}>
                <div style={{ fontSize:11, color:'#8b949e', letterSpacing:2, textTransform:'uppercase' }}>
                  {matchDetail.date} · {matchDetail.dom_ext} · {matchDetail.saison}
                </div>
                <div style={{ fontSize:13, fontWeight:600, margin:'4px 0' }}>{club.club || 'Nous'} vs {matchDetail.adversaire}</div>
                <div style={{ fontFamily:'Barlow Condensed', fontSize:42, fontWeight:800, margin:'6px 0', lineHeight:1 }}>
                  <span style={{ color: matchDetail.resultat==='V'?'#22c55e':matchDetail.resultat==='D'?'var(--danger)':'#eab308' }}>{matchDetail.score_nous}</span>
                  <span style={{ color:'#8b949e', fontSize:24 }}> — </span>
                  <span>{matchDetail.score_eux}</span>
                </div>
                <span className={`badge badge-${matchDetail.resultat}`}>{resLabel(matchDetail.resultat)}</span>
              </div>

              <div className="section-title">Stats tirs</div>
              <div className="card" style={{ padding:'6px 0' }}>
                <table className="stats-table">
                  <thead><tr><th>#</th><th>Joueur</th><th>Buts</th><th>Tirs</th><th>Eff.</th></tr></thead>
                  <tbody>
                    {(matchDetail.joueurs||[]).map((j,i) => {
                      const pct = j.tirs > 0 ? Math.round(j.buts/j.tirs*100) : 0;
                      return (
                        <tr key={j.id}>
                          <td style={{ color:'#8b949e', fontWeight:700 }}>{i+1}</td>
                          <td><div className="player-cell"><span className="num-badge">{j.numero}</span><span className="player-name">{j.nom}</span></div></td>
                          <td><span className="buts-val">{j.buts}</span></td>
                          <td>{j.tirs}</td>
                          <td><span className="pct-num" style={{ color:pctColor(pct) }}>{pct}%</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {tab === 'gardien' && (
        <div className="section">
          <div className="section-title">Nos gardiens — {saison}</div>
          {gardiens.map(g => (
            <div className="card" key={g.nom}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:10, marginBottom:14 }}>
                <div>
                  <div style={{ fontSize:10, color:'#8b949e', letterSpacing:2, textTransform:'uppercase' }}>Gardien</div>
                  <div style={{ fontFamily:'Barlow Condensed', fontSize:21, fontWeight:800, marginTop:2 }}>🧤 {g.nom}</div>
                  <div style={{ fontSize:12, color:'#8b949e' }}>{g.matchs_joues} match{g.matchs_joues>1?'s':''}</div>
                </div>
                <div style={{ background:gkColor(g.pct_arrets)+'1f', border:`1px solid ${gkColor(g.pct_arrets)}4d`, borderRadius:10, padding:'10px 18px', textAlign:'center' }}>
                  <div style={{ fontFamily:'Barlow Condensed', fontSize:30, fontWeight:800, color:gkColor(g.pct_arrets), lineHeight:1 }}>{g.pct_arrets}%</div>
                  <div style={{ fontSize:9, letterSpacing:1, color:gkColor(g.pct_arrets), marginTop:2 }}>ARRÊTS</div>
                </div>
              </div>
              <div style={{ display:'flex', gap:8 }}>
                {[['Tirs encaissés',g.tirs_encaisses],['Arrêts',g.arrets],['Buts encaissés',g.tirs_encaisses-g.arrets]].map(([l,v]) => (
                  <div key={l} style={{ flex:1, background:'#21262d', borderRadius:8, padding:'10px 6px', textAlign:'center' }}>
                    <div style={{ fontFamily:'Barlow Condensed', fontSize:19, fontWeight:800 }}>{v}</div>
                    <div style={{ fontSize:9, color:'#8b949e', letterSpacing:1, textTransform:'uppercase', marginTop:2 }}>{l}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:14 }}>
                <div style={{ height:7, background:'#21262d', borderRadius:6, overflow:'hidden' }}>
                  <div style={{ width:`${g.pct_arrets}%`, height:'100%', background:gkColor(g.pct_arrets), borderRadius:6, transition:'width 0.6s ease' }} />
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', marginTop:4, fontSize:10, color:'#8b949e' }}>
                  <span>0%</span><span>Référence: 33%</span><span>100%</span>
                </div>
              </div>
              <GardienEvolution nom={g.nom} saison={saison} />
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function BilanTirs({ tirs }) {
  const a = tirs.attaque, d = tirs.defense;

  const Barre = ({ segments, total }) => (
    <div style={{ display:'flex', height:22, borderRadius:6, overflow:'hidden', background:'#21262d' }}>
      {segments.filter(s => s.val > 0).map((s,i) => (
        <div key={i} title={`${s.label} : ${s.val}`}
          style={{ width:`${(s.val/total)*100}%`, background:s.col,
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:10, fontWeight:800, color:'#0d1117' }}>
          {(s.val/total) > 0.09 ? s.val : ''}
        </div>
      ))}
    </div>
  );

  const Legende = ({ items }) => (
    <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginTop:7 }}>
      {items.map((it,i) => (
        <span key={i} style={{ display:'flex', alignItems:'center', gap:5, fontSize:10.5, color:'#8b949e' }}>
          <span style={{ width:9, height:9, borderRadius:2, background:it.col, flexShrink:0 }} />
          {it.label} <b style={{ color:'#e6edf3' }}>{it.val}</b>
        </span>
      ))}
    </div>
  );

  return (
    <>
      <div className="card" style={{ marginBottom:8 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:9 }}>
          <span style={{ fontSize:12.5, fontWeight:700 }}>🤾 Notre attaque</span>
          <span style={{ fontFamily:'Barlow Condensed', fontSize:20, fontWeight:800, color:'#22c55e' }}>
            {a.pct_reussite}%
          </span>
        </div>
        <Barre total={a.tentatives} segments={[
          { label:'Buts', val:a.buts, col:'#22c55e' },
          { label:'Arrêtés par le gardien', val:a.arretes, col:'#eab308' },
          { label:'Ratés ou contrés', val:a.rates, col:'#4b5563' },
        ]} />
        <Legende items={[
          { label:'buts', val:a.buts, col:'#22c55e' },
          { label:'arrêtés', val:a.arretes, col:'#eab308' },
          { label:'ratés / contrés', val:a.rates, col:'#4b5563' },
        ]} />
        <div style={{ fontSize:10.5, color:'#6b7280', marginTop:7 }}>
          {a.tentatives} tentatives · {a.pct_reussite}% de réussite
        </div>
      </div>

      <div className="card">
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:9 }}>
          <span style={{ fontSize:12.5, fontWeight:700 }}>🛡️ Notre défense</span>
          <span style={{ fontFamily:'Barlow Condensed', fontSize:20, fontWeight:800,
            color: d.pct_stoppe >= 50 ? '#22c55e' : d.pct_stoppe >= 35 ? '#eab308' : '#ef4444' }}>
            {d.pct_stoppe}%
          </span>
        </div>
        <Barre total={d.tentatives} segments={[
          { label:'Arrêts du gardien', val:d.arrets, col:'#3b82f6' },
          { label:'Tirs contrés ou manqués', val:d.rates, col:'#8b5cf6' },
          { label:'Buts encaissés', val:d.encaisses, col:'#ef4444' },
        ]} />
        <Legende items={[
          { label:'arrêts gardien', val:d.arrets, col:'#3b82f6' },
          { label:'contrés / manqués', val:d.rates, col:'#8b5cf6' },
          { label:'encaissés', val:d.encaisses, col:'#ef4444' },
        ]} />
        <div style={{ fontSize:10.5, color:'#6b7280', marginTop:7, lineHeight:1.5 }}>
          {d.tentatives} tentatives adverses · {d.pct_stoppe}% stoppées par le gardien <b>et</b> le bloc défensif
        </div>
      </div>
    </>
  );
}

function GardienEvolution({ nom, saison }) {
  const [data, setData] = useState([]);
  useEffect(() => { getStatsGardien(nom, saison).then(setData); }, [nom, saison]);
  if (!data.length) return null;
  return (
    <div style={{ marginTop:16 }}>
      <div style={{ fontSize:10, color:'#8b949e', letterSpacing:2, marginBottom:8, textTransform:'uppercase' }}>Évolution par match</div>
      <ResponsiveContainer width="100%" height={160}>
        <LineChart data={data.map(e => ({ match:e.journee||e.date, '%':e.pct_arrets||0, Arrêts:e.arrets }))}>
          <CartesianGrid stroke="#21262d" strokeDasharray="3 3" />
          <XAxis dataKey="match" tick={{ fill:'#8b949e', fontSize:10 }} />
          <YAxis tick={{ fill:'#8b949e', fontSize:10 }} />
          <Tooltip contentStyle={{ background:'#161b22', border:'1px solid #21262d', borderRadius:8, fontSize:11 }} />
          <Line type="monotone" dataKey="%" stroke="#22c55e" strokeWidth={2} dot={{ r:3 }} name="% arrêts" />
          <Line type="monotone" dataKey="Arrêts" stroke="#3b82f6" strokeWidth={2} dot={{ r:3 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
